import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { SubscriptionType, User, Workspace } from "@models/user";
import { Wallet } from "@models/wallet";
import { BehaviorSubject, filter, Observable, take } from "rxjs";

import { LocalStorageService } from "./local-storage.service";
import { NotificationService } from "./notification.service";
import { PermissionService } from "./permissions.service";
import { WalletService } from "./wallet.service";

@Injectable({
    providedIn: "root"
})
export class SessionService {

    private userSubject: BehaviorSubject<User | null> = new BehaviorSubject(null);
    private workspaceSubject: BehaviorSubject<Workspace | null> = new BehaviorSubject(null);
    private userCache: User | null = null;
    private readonly isNativePlatform = Capacitor.isNativePlatform();
    private readonly userPrefsKey = "session_user";

    /** Emits whenever the signed-in user payload changes (login, refresh, profile update, logout). */
    public readonly user$: Observable<User | null> = this.userSubject.asObservable();

    /** Emits whenever the active workspace changes (login, switch, logout). */
    public readonly workspace$: Observable<Workspace | null> = this.workspaceSubject.asObservable();
    
    constructor(private localStorgeSvc: LocalStorageService,
                private notificationSvc: NotificationService,
                private permissionSvc: PermissionService
    ){
        if (this.isNativePlatform) {
            const user = this.readSessionStorageUser();
            this.userCache = user;
            if (user?.user?.name) {
                this.userSubject.next(user);
                this.handleWorkspaceSelectionOnRefresh(user);
            }
            return;
        }
        this.hydrateWebSession();
    }

    private hydrateWebSession(): void {
        const user = this.readUserFromWebStorage();
        this.userCache = user;
        if(user && user.user && user.user.name){
            this.userSubject.next(user);
            this.handleWorkspaceSelectionOnRefresh(user);
        }
    }

    private async hydrateNativeSession(): Promise<void> {
        const sessionUser = this.readSessionStorageUser();
        const user = sessionUser ?? await this.readUserFromPreferences();
        this.userCache = user;
        if (user?.user?.name) {
            this.userSubject.next(user);
            this.handleWorkspaceSelectionOnRefresh(user);
        }
    }

    /**
     * Handles workspace selection when the page is refreshed
     * Implements the same logic as the authentication service
     */
    private handleWorkspaceSelectionOnRefresh(user: User): void {
        if (!user.user || !user.user.workspaces || user.user.workspaces.length === 0) {
            return;
        }

        // Check if workspace is already set in session
        const currentWorkspace = this.getUserWorkspace();
        if (currentWorkspace && currentWorkspace.uid) {
            // Workspace is already set, no need to select again
            return;
        }

        let workspaceToSelect: Workspace | undefined;

        if (user?.user?.workspaces && user.user.workspaces.length > 0) {
            if (user.lastSelectedWorkspace && user.lastSelectedWorkspace > 0) {
                const lastWorkspace = user.user.workspaces.find(w => w.id === user.lastSelectedWorkspace);
                if (lastWorkspace) {
                    workspaceToSelect = lastWorkspace;
                }
            }
            if (!workspaceToSelect) {
                workspaceToSelect = user.user.workspaces[0];
            }
        }

        if (workspaceToSelect) {
            this.workspaceSubject.next(workspaceToSelect);
        }

    }
    
    public isUserLoggedIn(){
        return <User>this.userSubject.value && (<User>this.userSubject.value).user && (<User>this.userSubject.value).user.name
    }

    public removeUser() {
        this.userCache = null;
        this.userSubject.next(null);
        this.workspaceSubject.next(null);
        this.notificationSvc.hideNotification();
        if (this.isNativePlatform) {
            sessionStorage.removeItem('user');
            void Preferences.remove({ key: this.userPrefsKey });
        } else {
            this.localStorgeSvc.removeItem('user');
            sessionStorage.removeItem('user');
        }
        
        // Also clear permissions when user is removed
        this.permissionSvc.clearPermissions();
    }

    /** Sets active workspace for the user after user logs in */
    public setUserWorkspace(workspace: Workspace) {
        this.workspaceSubject.next(workspace);
    }

    public getUserWorkspace() {
        return <Workspace>this.workspaceSubject.value;
    }

    /**
     * Getter and setter for user values
     */
    public getUserValue(): User | null {
        if (this.userCache) {
            return this.userCache;
        }
        if (this.isNativePlatform) {
            const sessionUser = this.readSessionStorageUser();
            if (sessionUser) {
                this.userCache = sessionUser;
                return sessionUser;
            }
            // Async hydrate for native persisted session if cache is cold.
            void this.hydrateNativeSession();
            return null;
        }
        const user = this.readUserFromWebStorage();
        this.userCache = user;
        if (user) return user;
        return null;
    }

    public setUserValue(user: User, rememberMe: boolean) {
        this.userCache = user;
        if (this.isNativePlatform) {
            if (rememberMe) {
                void this.writeUserToPreferences(user);
                sessionStorage.removeItem('user');
            } else {
                sessionStorage.setItem('user', JSON.stringify(user));
                void Preferences.remove({ key: this.userPrefsKey });
            }
        } else if(rememberMe) {
            this.localStorgeSvc.setItem("user", user);
            sessionStorage.removeItem('user');
        }
        else{
            sessionStorage.setItem('user', JSON.stringify(user));
            this.localStorgeSvc.removeItem('user');
        }
        this.userSubject.next(user);
    } 

    /**
     * Update user data in session storage and subject
     */
    public updateUserData(user: User) {
        this.userCache = user;
        if (this.isNativePlatform) {
            const hasSessionUser = !!sessionStorage.getItem('user');
            if (hasSessionUser) {
                sessionStorage.setItem('user', JSON.stringify(user));
            } else {
                void this.writeUserToPreferences(user);
            }
        } else {
            // Update in localStorage if exists
            const existingUser = this.localStorgeSvc.getItem("user");
            if (existingUser) {
                this.localStorgeSvc.setItem("user", user);
            }

            // Update in sessionStorage if exists
            const sessionUser = sessionStorage.getItem('user');
            if (sessionUser) {
                sessionStorage.setItem('user', JSON.stringify(user));
            }
        }

        // Update the subject
        this.userSubject.next(user);

        // Keep active workspace in sync with the latest user payload (nested workspaces are updated here
        // while `workspaceSubject` may still point at an older object graph until `setUserWorkspace` runs).
        const active = this.workspaceSubject.value;
        if (active && user.user?.workspaces?.length) {
            const refreshed =
                (active.uid ? user.user.workspaces.find((w) => w.uid === active.uid) : undefined) ??
                (active.id != null ? user.user.workspaces.find((w) => w.id === active.id) : undefined);
            if (refreshed) {
                this.workspaceSubject.next(refreshed);
            }
        }
    }

    private readUserFromWebStorage(): User | null {
        const localUser = this.localStorgeSvc.getItem("user");
        if (localUser) {
            return localUser;
        }
        return this.readSessionStorageUser();
    }

    private readSessionStorageUser(): User | null {
        const raw = sessionStorage.getItem('user');
        if (!raw) {
            return null;
        }
        try {
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }

    private async readUserFromPreferences(): Promise<User | null> {
        try {
            const { value } = await Preferences.get({ key: this.userPrefsKey });
            if (!value) {
                return null;
            }
            return JSON.parse(value);
        } catch {
            return null;
        }
    }

    private async writeUserToPreferences(user: User): Promise<void> {
        try {
            await Preferences.set({
                key: this.userPrefsKey,
                value: JSON.stringify(user),
            });
        } catch {
            // no-op; callers still get in-memory/session state
        }
    }

}